import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import CommonTable from "../../../components/Common/TableContainer";
import { submitTaskGroupSettingsFilterData } from "model/TaskGroupSettingsModel";
import TaskGroupAddEditModal from "pages/AllCommonModal/TaskGroupAddEditModal/task-group-add-edit-modal";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useTaskGroupSettingsContext } from "libs/context/TaskGroupSettingsContext";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

const resolveFilterRows = (payload) => {
  const raw = payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : raw?.records ?? raw?.rows ?? [];
  return Array.isArray(list) ? list : [];
};

const TaskGroupSettings = () => {
  const { permissionData } = usePermissionContext() || {};
  const {
    fetchTaskGroupSettingsContextData,
    taskGroupSettingsContextData,
  } = useTaskGroupSettingsContext() || {};

  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [groupId, setGroupId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [taskGroupSettingsData, setTaskGroupSettingsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    if (!searchQuery?.trim()) return taskGroupSettingsData;

    return taskGroupSettingsData?.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [taskGroupSettingsData, searchQuery]);

  const actionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.MasterTaskBucketAccess),
      canEdit: Boolean(permissionData?.MasterTaskBucketAccess),
    }),
    [permissionData]
  );

  const hasAnyRowAction = useMemo(
    () => actionPermissions.canEdit,
    [actionPermissions]
  );

  const getTaskGroupSettingsList = async (payload = {}) => {
    setLoading(true);
    const response = await submitTaskGroupSettingsFilterData(payload);
    const listData = resolveFilterRows(response);
    const valueReModified = listData.map((item) => ({
      ...item,
      ID: item?.id ?? item?.groupId,
    }));

    if (response) {
      setTaskGroupSettingsData(valueReModified);
    } else {
      setTaskGroupSettingsData([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchTaskGroupSettingsContextData?.();
    getTaskGroupSettingsList({});
  }, []);

  useEffect(() => {
    const valueReModified = taskGroupSettingsContextData?.table?.columns?.map(
      (col) => ({
        Header: col?.title,
        accessor: col?.field,
        type: col?.type,
      })
    );
    setTableMetadata(valueReModified);
  }, [taskGroupSettingsContextData]);

  const handleEdit = (row) => {
    const resolvedGroupId =
      row?.groupId ??
      row?.group?.id ??
      row?.taskBucketGroupId ??
      row?.TaskBucketGroupId ??
      row?.id ??
      null;
    setGroupId(resolvedGroupId);
    setEditRow(row);
    setAddEditModalOpen(true);
  };

  const handleCreate = () => {
    setGroupId(null);
    setEditRow(null);
    setAddEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setAddEditModalOpen(false);
    setGroupId(null);
    setEditRow(null);
  };

  const handleFilterSubmit = async (data) => {
    await getTaskGroupSettingsList(data);
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    await getTaskGroupSettingsList({
      search: value,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">Task Group Settings</h5>

                {actionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button color="primary" onClick={handleCreate}>
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />
                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && taskGroupSettingsData?.length && tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyRowAction}
                  onEdit={actionPermissions.canEdit ? handleEdit : undefined}
                  isEditAccess={actionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={false}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName="Task Group Settings"
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <TaskGroupAddEditModal
            isOpen={addEditModalOpen}
            onClose={handleCloseModal}
            groupId={groupId}
            editRow={editRow}
            onSuccess={() => getTaskGroupSettingsList({})}
          />

          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Task Group Settings"
            handleSubmit={handleFilterSubmit}
            filterData={taskGroupSettingsContextData?.filters}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

TaskGroupSettings.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(TaskGroupSettings);
